import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Switch,
  FormControlLabel,
  Tooltip,
  Box,
  Avatar,
  IconButton,
  RadioGroup,
  CircularProgress,
  Radio,
  Grid,
  ListSubheader,
  InputAdornment,
  InputLabel,
} from "@mui/material";
import AssistantExamplesSection from "./AssistantExamplesSection";

import { ref, uploadBytes, getDownloadURL } from "./firebase";
import LoadingDialog from "./LoadingDialog";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import axios from "axios";
import { BackButton } from "./functions";

import { AnimatedStep } from "./Animations";
import AddIcon from "@mui/icons-material/Add";
import { trackLesson } from "./functions";
import ExampleDescriptionsDialog from "./ExampleDescriptionsDialog";

import { db, storage } from "./firebase";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  setDoc,
  getDocs,
} from "firebase/firestore";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import { ThemeSelectorFlow } from "./ThemeSelectorFlow";
import PublicIcon from "@mui/icons-material/Public";
import InfoBox from "./InfoBox";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LockIcon from "@mui/icons-material/Lock";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { getStyles } from "../index";
import IdeaGenerator from "./IdeaGenerator";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LoadingSpinner from "./LoadingSpinner";
import GuideSection from "./GuideSection"; // Adjust the import path as needed
import { useSnackbar } from "./SnackbarHandler";
import Suggestions from "./Suggestions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExploreIcon from "@mui/icons-material/Explore";
import Logger from "./Logger";
import { sanitizeInput } from "./functions";
import GenerateCharacter from "./GenerateCharacter";

const TeacherHandler = ({ teacherId, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [isExampleDialogOpen, setIsExampleDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const [customTheme, setCustomTheme] = useState("");
  const [lessonTheme, setLessonTheme] = useState("");
  const [hoveredBox, setHoveredBox] = useState(null);

  const [predefinedTheme, setPredefinedTheme] = useState(null);
  const transcriptRef = useRef("");

  const uploadedFileRef = useRef("");

  const wikipediaContentRef = useRef("");
  const randomizedContentRef = useRef("");
  const coursePlanContentRef = useRef("");

  const urlContentRef = useRef("");
  const bookContentRef = useRef("");

  const analysisContentRef = useRef("");
  const pasteTextContentRef = useRef("");
  const resetThemeContent = () => {
    setLessonTheme("");
    setCustomTheme("");
    setPredefinedTheme(null);
    transcriptRef.current = "";
    uploadedFileRef.current = "";
    wikipediaContentRef.current = "";
    randomizedContentRef.current = "";
    bookContentRef.current = "";
    urlContentRef.current = "";
    analysisContentRef.current = "";
    coursePlanContentRef.current = "";
    pasteTextContentRef.current = "";
  };

  const styles = getStyles(theme);

  const [teacherData, setTeacherData] = useState({
    characterName: "",
    tagline: "",
    description: "",
    greeting: "",
    voice: "",
    teachingStyle: "",
    personality: "",
    pedagogicalPhilosophy: "",
    communicationStyle: "",
    background: "",
    approachToMistakes: "",
    dyslexiaAdapted: false,
    adhdAdapted: false,
    imageUrl: "",
    category: "",
    languageToLearn: "",
    traditionalTeacherSubject: "",

    backgroundImage: "", // New field for background image
    type: "", // New field for type dropdown
    cardDescription: "",
    rag: false,
  });

  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [visibility, setVisibility] = useState("public");
  const [viewIsLoading, setViewIsLoading] = useState(true);
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
    imageUrl: "",
  });
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryImage, setCategoryImage] = useState(null);
  const [categoryPreviewUrl, setCategoryPreviewUrl] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundPreviewUrl, setBackgroundPreviewUrl] = useState(null);

  const handleBackgroundImageChange = (e) => {
    if (e.target.files[0]) {
      setBackgroundImage(e.target.files[0]);
      setBackgroundPreviewUrl(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleIdeaGenerated = (idea) => {
    setTeacherData((prevData) => ({
      ...prevData,
      background: idea,
    }));
  };

  const maxLengths = {
    characterName: 50,
    tagline: 100,
    description: 500,
    greeting: 200,
    teachingStyle: 100,
    personality: 100,
    pedagogicalPhilosophy: 300,
    communicationStyle: 100,
    background: 1000,
    approachToMistakes: 300,
    cardDescription: 300,
    languageToLearn: 300,
    traditionalTeacherSubject: 300,
  };

  useEffect(() => {
    const fetchTeacherData = async () => {
      if (teacherId) {
        Logger.log("teacherId:", teacherId);
        const docRef = doc(db, "teachers", teacherId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Set a default value for cardDescription if it doesn't exist
          Logger.log("lärare hittad:", data);
          const updatedData = {
            ...data,
            cardDescription: data.cardDescription || "",
          };
          setTeacherData(updatedData);
          setPreviewUrl(updatedData.imageUrl);
          // Set background image preview if exists
          if (updatedData.backgroundImage) {
            setBackgroundPreviewUrl(updatedData.backgroundImage);
          }
          setVisibility(updatedData.visibility || "public");
        }
      } else {
        // Reset state when not editing (i.e., adding new teacher)
        setTeacherData({
          characterName: "",
          tagline: "",
          description: "",
          greeting: "",
          voice: "",
          teachingStyle: "",
          personality: "",
          pedagogicalPhilosophy: "",
          communicationStyle: "",
          background: "",
          approachToMistakes: "",
          dyslexiaAdapted: false,
          adhdAdapted: false,
          imageUrl: "",
          category: "",
          backgroundImage: "", // Reset background image
          type: "", // Reset type
          cardDescription: "",
          languageToLearn: "",
          traditionalTeacherSubject: "",
        });
        setPreviewUrl(null);
        setBackgroundPreviewUrl(null);
        setVisibility("public");
        setShowAdditionalFields(false);
        setImage(null);
        setBackgroundImage(null);
      }
    };

    fetchTeacherData();
  }, [teacherId, location.pathname]);

  useEffect(() => {
    setViewIsLoading(false);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      const categoriesCollection = collection(db, "categories");
      const categoriesSnapshot = await getDocs(categoriesCollection);
      const categoriesList = categoriesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoriesList);
    };

    fetchCategories();
  }, []);

  if (viewIsLoading) {
    return <LoadingSpinner />;
  }

  const handleExampleSelect = (description) => {
    setTeacherData((prev) => ({
      ...prev,
      background: description,
    }));
  };

  const handleCharacterGenerated = (characterData) => {
    setTeacherData({
      ...teacherData,
      characterName: characterData.characterName,
      description: characterData.description,
      greeting: characterData.greeting,
      background: characterData.background,
      tagline: characterData.tagline,
      visibility: characterData.visibility,
      cardDescription: characterData.cardDescription || "", // Add this line
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setTeacherData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImage(e.target.files[0]);
      setPreviewUrl(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleVisibilityChange = (event) => {
    setVisibility(event.target.value);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      if (!user) {
        console.error("Ingen användare inloggad");
        return;
      }

      // Check if character name or greeting is empty
      if (!teacherData.characterName.trim() || !teacherData.greeting.trim()) {
        showSnackbar("Namn och hälsningsfras är obligatoriska fält.", "info");
        setIsLoading(false);
        return;
      }

      let imageUrl = teacherData.imageUrl;
      if (image) {
        const imageRef = ref(
          storage,
          `teacherImages/${user.uid}/${image.name}`,
        );
        await uploadBytes(imageRef, image);
        imageUrl = await getDownloadURL(imageRef);
      }

      // Handle background image upload
      let backgroundImageUrl = teacherData.backgroundImage || "";
      if (backgroundImage) {
        const backgroundImageRef = ref(
          storage,
          `teacherBackgrounds/${user.uid}/${backgroundImage.name}`,
        );
        await uploadBytes(backgroundImageRef, backgroundImage);
        backgroundImageUrl = await getDownloadURL(backgroundImageRef);
      }

      // If no image is selected or uploaded, set imageUrl to a string that will trigger MUI's default avatar
      if (!imageUrl) {
        imageUrl = teacherData.characterName[0].toUpperCase();
      }

      // Sanitize all text fields
      const sanitizedTeacherData = Object.fromEntries(
        Object.entries(teacherData).map(([key, value]) => {
          if (typeof value === "string") {
            return [key, sanitizeInput(value)];
          }
          return [key, value];
        }),
      );

      let content = "";

      if (lessonTheme === "youtube") {
        content = transcriptRef.current;
      } else if (lessonTheme === "customTheme") {
        content = customTheme;
      } else if (lessonTheme === "predefined") {
        content = predefinedTheme;
      } else if (lessonTheme === "fileUpload") {
        content = uploadedFileRef.current;
      } else if (lessonTheme === "wikipedia") {
        content = wikipediaContentRef.current;
      } else if (lessonTheme === "randomizer") {
        content = randomizedContentRef.current;
      } else if (lessonTheme === "book") {
        content = bookContentRef.current;
      } else if (lessonTheme === "url") {
        content = urlContentRef.current;
      } else if (lessonTheme === "vision") {
        content = analysisContentRef.current;
      } else if (lessonTheme === "pasteText") {
        content = pasteTextContentRef.current;
      } else {
        //
        // content = coursePlanContentRef.current;
      }

      Logger.log("themeToUse: ", content);

      const teacherDataToSave = {
        ...sanitizedTeacherData,
        imageUrl,
        backgroundImage: backgroundImageUrl, // Add background image URL
        userId: user.uid,
        updatedAt: new Date(),
        visibility: visibility,
        isPublicCopy: false,
        category: teacherData.category || "",
        type: teacherData.type || "",
        rag: currentStep == 3 ? true : teacherData.rag || false,
        ragNamespace: currentStep === 3 ? "userData" : "",
        ragPrompt:
          currentStep === 3
            ? `Använda följande information för att besvara användarens fråga. \n\nNär du svarar: \n\n1. Inkludera direkta citat från texten inom citationstecken för att ge konkreta exempel \n\n2. Referera till specifika stycken från kontexten med deras nummer [1], [2], etc. \n\n3. Var tydlig med vilka delar som är textens egna ord och vilka som är dina sammanfattningar. \n\n4. Viktig information ska vara omslutet av fetstil markdown (**) så det är tydligt för användaren att läsa. `
            : "",

        ragContentTrainedOn: currentStep === 3 ? content : "",
      };

      let newTeacherId;
      if (teacherId) {
        await updateDoc(doc(db, "teachers", teacherId), teacherDataToSave);
        newTeacherId = teacherId;
        console.log("Document successfully updated: ", teacherDataToSave);
      } else {
        const docRef = await addDoc(collection(db, "teachers"), {
          ...teacherDataToSave,
          createdAt: new Date(),
        });
        newTeacherId = docRef.id;

        await trackLesson(user.uid, "TeacherHandler.js", null, {
          ...teacherDataToSave,
        });
      }

      if (teacherId && visibility === "public") {
        await updateDoc(
          doc(db, "publicTeachers", teacherId),
          teacherDataToSave,
        );
      }

      if (!teacherId) {
        if (visibility === "public") {
          await setDoc(doc(db, "publicTeachers", newTeacherId), {
            ...teacherDataToSave,
            originalCreatorId: user.uid,
          });
        }
      }

      // Om det är train assistant mode (step 3), träna assistenten i pinecone:
      if (currentStep === 3) {
        await trainAssistent(content, {
          teacherId: newTeacherId,
          userId: user.uid,
          namespace: "userData",
        });

        await trackLesson(user.uid, "TeacherHandlerTrained.js", null, {
          ...teacherDataToSave,
        });
      }

      Logger.log("teacherDataToSave: ", teacherDataToSave);

      if (teacherId) {
        showSnackbar(
          `${sanitizedTeacherData.characterName} är uppdaterad.`,
          "info",
        );
      } else {
        showSnackbar(
          `${sanitizedTeacherData.characterName} har stigit in i klassrummet.`,
          "success",
        );
      }
      setIsLoading(false);
      navigate(`/app/chat/${newTeacherId}`);
    } catch (error) {
      setIsLoading(false);
      console.error("Fel vid hantering av karaktär: ", error);
      showSnackbar("Ett fel uppstod. Försök igen.", "error");
    }
  };

  // Plain text upload function for AI assistant training
  // Metadata should contain userId and assistentId
  // const metadata = {
  //    namespace: "userData",
  //    userId: "",
  //    assistentId: "",

  //  };
  const trainAssistent = async (text, metadata) => {
    // Basic validation
    if (!text) throw new Error("Text is required");
    if (!metadata.namespace)
      throw new Error("namespace is required in metadata");

    try {
      // Split text into chunks
      const paragraphs = text.split(/\n\s*\n/);
      const chunks = [];
      let currentChunk = "";
      let chunkSize = 1000;

      for (const paragraph of paragraphs) {
        if (currentChunk.length + paragraph.length + 2 > chunkSize) {
          chunks.push(currentChunk);
          currentChunk = paragraph;
        } else {
          currentChunk += (currentChunk ? "\n\n" : "") + paragraph;
        }
      }

      if (currentChunk) {
        chunks.push(currentChunk);
      }

      // Create document objects - much simpler
      const documents = chunks.map((chunk, index) => ({
        text: chunk,
        source: metadata.source || "user-input",
        id: `${metadata.id || Date.now()}-${index}`,
        position: index,
        userId: metadata.userId,
        teacherId: metadata.teacherId,
      }));

      const namespaceToUse = "userData";

      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

      // Send to API
      const response = await axios.post(`${API_URL}/api/documents/upload`, {
        documents,
        metadata: {
          ...metadata,
          namespace: namespaceToUse,
        },
      });

      return response.data;
    } catch (err) {
      console.error("Upload error:", err);
      //throw err;
    }
  };

  const handleSelect = (selectedItem, listType) => {
    Logger.log(selectedItem);
    setTeacherData({
      characterName: selectedItem.characterName || "",
      tagline: selectedItem.tagline || "",
      description: selectedItem.description || "",
      greeting: selectedItem.greeting || "",
      teachingStyle: selectedItem.teachingStyle || "",
      personality: selectedItem.personality || "",
      pedagogicalPhilosophy: selectedItem.pedagogicalPhilosophy || "",
      communicationStyle: selectedItem.communicationStyle || "",
      background: selectedItem.background || "",
      approachToMistakes: selectedItem.approachToMistakes || "",
      dyslexiaAdapted: selectedItem.dyslexiaAdapted || false,
      adhdAdapted: selectedItem.adhdAdapted || false,
      imageUrl: selectedItem.imageUrl || "",
      category: selectedItem.category || "",
      backgroundImage: selectedItem.backgroundImage || "",
      type: selectedItem.type || "",
      cardDescription: selectedItem.cardDescription || "",
      rag: selectedItem.rag || false,
    });

    setVisibility(selectedItem.visibility || "public");

    if (selectedItem.imageUrl) {
      setPreviewUrl(selectedItem.imageUrl);
    }

    if (selectedItem.backgroundImage) {
      setBackgroundPreviewUrl(selectedItem.backgroundImage);
    }

    Logger.log("Valt objekt:", selectedItem);
  };

  const handleCategoryChange = (e) => {
    const { name, value } = e.target;
    setNewCategory((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryImageChange = (e) => {
    if (e.target.files[0]) {
      setCategoryImage(e.target.files[0]);
      setCategoryPreviewUrl(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleAddCategory = async () => {
    try {
      const sanitizedCategory = {
        name: sanitizeInput(newCategory.name),
        description: sanitizeInput(newCategory.description),
      };

      let imageUrl = "";
      if (categoryImage) {
        const imageRef = ref(storage, `categoryImages/${categoryImage.name}`);
        await uploadBytes(imageRef, categoryImage);
        imageUrl = await getDownloadURL(imageRef);
      }

      const categoryToSave = {
        ...sanitizedCategory,
        imageUrl,
      };

      const docRef = await addDoc(collection(db, "categories"), categoryToSave);
      setCategories((prev) => [...prev, { id: docRef.id, ...categoryToSave }]);
      setNewCategory({ name: "", description: "", imageUrl: "" });
      setCategoryImage(null);
      setCategoryPreviewUrl(null);
      setShowCategoryForm(false);
      showSnackbar("Kategori har lagts till", "success");
    } catch (error) {
      console.error("Fel vid tillägg av kategori: ", error);
      showSnackbar("Ett fel uppstod. Försök igen.", "error");
    }
  };

  return (
    <Box sx={{ ...styles.container }}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "100%" },
          margin: "0 auto",
        }}
      >
        <Typography
          variant="h1"
          sx={{
            ...styles.newH1,
          }}
        >
          {teacherId ? "Redigera karaktär" : "Skapa AI-assistent"}
        </Typography>
        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          <Button
            variant="purp"
            onClick={(e) => {
              e.preventDefault();
              navigate("/app/personal");
            }}
            startIcon={<ExploreIcon />}
            sx={{ ...styles.tightButton, display: "none" }}
          >
            Alla assistenter
          </Button>
        </Box>
        <InfoBox
          title="Quiz"
          instructions={[
            "Välj underlag till ditt quiz, eller skriv in manuella instruktioner",
            "Välj att generera frågor, redigera & anpassa, dela med elever, spara eller skriv ut",
            "Se din historik över resultat, senaste träningstillfället och mycket mer",
          ]}
          theme={theme}
          styles={styles}
          user={user}
          assistent={true}
        />
        {currentStep === 0 && (
          <Grid container spacing={4} sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <Box
                onClick={() => setCurrentStep(2)}
                onMouseEnter={() => setHoveredBox("train")}
                onMouseLeave={() => setHoveredBox(null)}
                sx={{
                  ...styles.freshBox,
                  backgroundColor: "#805ff41a",
                  boxShadow: "none",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  height: { xs: "auto", md: "390px" },
                  "&:hover": {
                    backgroundColor: "#805ff429",
                  },
                  "&:hover .plusIcon": {
                    transform: "rotate(90deg)",
                  },
                }}
              >
                <IconButton
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: "#fff",
                    width: "64px",
                    height: "64px",
                    mb: 2,
                    transition: "transform 0.3s ease",
                  }}
                  className="plusIcon"
                >
                  <AddIcon sx={{ fontSize: 32 }} />
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.5rem",
                    textAlign: "center",
                  }}
                >
                  Träna en AI-assistent på eget dokument
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mt: 2,
                    color: "text.secondary",
                    textAlign: "center",
                    fontSize: "0.9rem",
                    opacity: hoveredBox === "train" ? 1 : 0,
                    maxHeight: hoveredBox === "train" ? "200px" : "0px",
                    overflow: "hidden",
                    transition:
                      "opacity 0.3s ease-in-out, max-height 0.3s ease-in-out",
                  }}
                >
                  Ladda upp dina dokument och låt vår AI-assistent göra
                  informationen tillgänglig genom en enkel konversation. Ställ
                  frågor, få svar och utforska ditt material - utan att behöva
                  söka och skrolla. Din personliga dokumentguide förstår
                  innehållet på djupet och hjälper dig hitta precis det du
                  behöver, när du behöver det.
                </Typography>
              </Box>
              {/* Examples section BELOW this box */}
              <AssistantExamplesSection
                title="Exempel på tränade assistenter"
                examples={[
                  {
                    name: "Lagboken",
                    description: "Tränad på lagboken",
                    image:
                      "https://firebasestorage.googleapis.com/v0/b/studeraai.appspot.com/o/teacherImages%2FZ6BLSMsm38Un5mgW0O6Z3TToHq12%2Flagg.jpg?alt=media&token=6bb4e135-3670-4790-8d95-bc3bb678e70d",
                    url: "/app/chat/xCiKaUwD1UBUM8lwhQNe",
                  },
                  {
                    name: "Bibeln",
                    description: "Tränad på Bibeln 2000",
                    image:
                      "https://firebasestorage.googleapis.com/v0/b/studeraai.appspot.com/o/teacherImages%2FZ6BLSMsm38Un5mgW0O6Z3TToHq12%2Fgetimg_ai_img-x7XN8Uv36HUKc5Z21Izeb.jpeg?alt=media&token=a4261993-fdd6-4fa3-9fb3-cf7ce5e35308",
                    url: "/app/chat/MzgqU3sov4MVvJyUZ4xL",
                  },
                ]}
                navigate={navigate}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                onClick={() => setCurrentStep(1)}
                onMouseEnter={() => setHoveredBox("simple")}
                onMouseLeave={() => setHoveredBox(null)}
                sx={{
                  ...styles.freshBox,
                  backgroundColor: "#a2a2a21a",
                  boxShadow: "none",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  height: { xs: "auto", md: "390px" },
                  "&:hover": {
                    backgroundColor: "#66666629",
                  },
                  "&:hover .plusIcon": {
                    transform: "rotate(90deg)",
                  },
                }}
              >
                <IconButton
                  sx={{
                    backgroundColor: "text.secondary",
                    color: "#fff",
                    width: "64px",
                    height: "64px",
                    mb: 2,
                    transition: "transform 0.3s ease",
                  }}
                  className="plusIcon"
                >
                  <AddIcon sx={{ fontSize: 32 }} />
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.5rem",
                    textAlign: "center",
                  }}
                >
                  Skapa en enkel AI-assistent
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mt: 2,
                    color: "text.secondary",
                    textAlign: "center",
                    fontSize: "0.9rem",
                    opacity: hoveredBox === "simple" ? 1 : 0,
                    maxHeight: hoveredBox === "simple" ? "200px" : "0px",
                    overflow: "hidden",
                    transition:
                      "opacity 0.3s ease-in-out, max-height 0.3s ease-in-out",
                  }}
                >
                  Skapa en enklare assistent där du med din egen kreativa
                  förmåga kan specificera hur assistenten ska svara. T.ex. kan
                  du instruera den att alltid svara på två språk, begränsa vad
                  den får svara på, att alltid ställa en viss följdfråga, att
                  anta en specifik personlighet när den svarar eller att
                  formatera svaren på ett bestämt sätt.
                </Typography>
              </Box>
              {/* Examples section BELOW this box */}
              <AssistantExamplesSection
                title="Exempel på enkla assistenter"
                examples={[
                  {
                    name: "Virginia Woolf",
                    description: "",
                    image:
                      "https://firebasestorage.googleapis.com/v0/b/studeraai.appspot.com/o/misc%2Fnew%20batch%2FVirginia%20Woolf.png?alt=media&token=4002e5d0-d43f-40fe-b3d8-4b494920e901",
                    url: "/app/chat/jQOVP4WABFtWpIkHZWYD",
                  },
                  {
                    name: "Ingmar Bergman",
                    description: "",
                    image:
                      "https://firebasestorage.googleapis.com/v0/b/studeraai.appspot.com/o/misc%2Fib.png?alt=media&token=40e17a71-6d4f-4627-8e57-edd532ac29ba",
                    url: "/app/chat/fUmhgXxjiUs3OMVbqujs",
                  },
                ]}
                navigate={navigate}
              />
            </Grid>
          </Grid>
        )}
        {currentStep === 2 && (
          <>
            <BackButton
              currentStep={currentStep}
              onClick={() => {
                setCurrentStep(0);

                resetThemeContent();
              }}
              show={currentStep !== 1}
            />
            <AnimatedStep isRevealed={true}>
              <Box sx={{ width: { xs: "100%", md: "100%" }, margin: "0 auto" }}>
                <ThemeSelectorFlow
                  value={lessonTheme}
                  onChange={(e) => setLessonTheme(e.target.value)}
                  customTheme={customTheme}
                  onCustomThemeChange={(e) => {
                    Logger.log("onCustomThemeChange", e.target.value);
                    setCustomTheme(e.target.value);
                    setCurrentStep(3);
                  }}
                  onPastedTextChange={(content) => {
                    Logger.log("onPastedTextChange", content.target.value);
                    pasteTextContentRef.current = content.target.value;
                    setCurrentStep(3);
                  }}
                  onAnalysisContentChange={(content) => {
                    Logger.log("onAnalysisContentChange", content.analysis);
                    analysisContentRef.current = content.analysis;
                    setCurrentStep(3);
                  }}
                  onBookContentChange={(content) => {
                    Logger.log(
                      "onBookContentChange",
                      content.substring(0, 100),
                    );
                    bookContentRef.current = content;
                    setCurrentStep(3);
                  }}
                  onUrlContentChange={(content) => {
                    Logger.log(
                      "Parent: URL content received",
                      content.substring(0, 300),
                    );
                    urlContentRef.current = content;
                    setCurrentStep(3);
                  }}
                  onPredefinedThemeChange={(e) =>
                    setPredefinedTheme(e.target.value)
                  }
                  onYoutubeTranscriptChange={(transcriptContent) => {
                    Logger.log(
                      "onYoutubeTranscriptChange körs med: ",
                      transcriptContent,
                    );
                    transcriptRef.current = transcriptContent;
                    setCurrentStep(3);
                  }}
                  onFileUpload={(content) => {
                    Logger.log("onFileUpload körs med: ", content);
                    uploadedFileRef.current = content;
                    setCurrentStep(3);
                  }}
                  onWikipediaContentChange={(content) => {
                    Logger.log(
                      "Parent: Wikipedia content received",
                      content.substring(0, 100),
                    );
                    wikipediaContentRef.current = content;
                    setCurrentStep(3);
                  }}
                  onRandomizedContentChange={(content) => {
                    Logger.log(
                      "randomizedContentRef",
                      content.substring(0, 100),
                    );
                    randomizedContentRef.current = content;
                    setCurrentStep(3);
                  }}
                  onCoursePlanContentChange={(content) => {
                    Logger.log("onCoursePlanChange", content.substring(0, 100));
                    coursePlanContentRef.current = content;
                    setCurrentStep(3);
                  }}
                  lessonType={"TeacherHandler.js"}
                  user={user}
                  theme={theme}
                  styles={styles}
                  forTeachers={true}
                />
              </Box>
            </AnimatedStep>
          </>
        )}

        {(currentStep === 1 || currentStep === 3) && (
          <>
            <BackButton
              currentStep={currentStep}
              onClick={() => {
                setCurrentStep(0);

                resetThemeContent();
              }}
            />
            <AnimatedStep isRevealed={true}>
              <Grid
                container
                spacing={3}
                alignItems="center"
                justifyContent="center"
              >
                <Grid item xs={12} md={8} sx={{ mt: 5 }}>
                  {/* Formulärsektionen */}
                  <Box>
                    {/* Category Form */}
                    {user.admin && (
                      <Button
                        onClick={() => setShowCategoryForm(!showCategoryForm)}
                        variant="outlined"
                        sx={{
                          ...styles.beautyBtnTres,
                          mt: 2,
                          mb: 2,
                        }}
                      >
                        {showCategoryForm
                          ? "Dölj kategoriformat"
                          : "Lägg till ny kategori"}
                      </Button>
                    )}

                    {showCategoryForm && (
                      <Box
                        sx={{
                          mb: 4,
                          p: 2,
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      >
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Lägg till ny kategori
                        </Typography>

                        <TextField
                          fullWidth
                          margin="normal"
                          name="name"
                          label="Kategorinamn"
                          value={newCategory.name}
                          onChange={handleCategoryChange}
                          sx={{ ...styles.whiteBg, mb: 2 }}
                        />

                        <TextField
                          fullWidth
                          margin="normal"
                          name="description"
                          label="Beskrivning"
                          multiline
                          rows={2}
                          value={newCategory.description}
                          onChange={handleCategoryChange}
                          sx={{ ...styles.whiteBg, mb: 2 }}
                        />

                        <Box sx={{ mb: 2 }}>
                          <input
                            accept="image/*"
                            type="file"
                            onChange={handleCategoryImageChange}
                            style={{ display: "none" }}
                            id="category-image-upload"
                          />
                          <label htmlFor="category-image-upload">
                            <IconButton component="span">
                              <Avatar
                                sx={{
                                  width: 100,
                                  height: 100,
                                  bgcolor: "#8b5cf6",
                                  color: "white",
                                  cursor: "pointer",
                                }}
                              >
                                {categoryPreviewUrl ? (
                                  <img
                                    src={categoryPreviewUrl}
                                    alt="Category Preview"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <AddAPhotoIcon sx={{ fontSize: 40 }} />
                                )}
                              </Avatar>
                            </IconButton>
                          </label>
                        </Box>

                        <Button
                          onClick={handleAddCategory}
                          variant="contained"
                          color="primary"
                          sx={{ mt: 2 }}
                        >
                          Lägg till kategori
                        </Button>
                      </Box>
                    )}

                    <Box sx={{ mb: 5 }}></Box>
                    <Box
                      sx={{
                        //display: "flex",
                        display: "none",
                        justifyContent: "flex-end",
                        marginBottom: "10px",
                      }}
                    >
                      <Suggestions
                        listType="teachers2"
                        maxSuggestions={6}
                        onSelect={handleSelect}
                        alignRight={true}
                      />
                    </Box>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Typography variant="formLabelSinUnderline">
                          Assistentens namn*
                        </Typography>
                        <TextField
                          fullWidth
                          margin="normal"
                          name="characterName"
                          value={teacherData.characterName}
                          onChange={handleChange}
                          inputProps={{ maxLength: maxLengths.characterName }}
                          sx={{ ...styles.whiteBg, mt: 1 }}
                          placeholder="Namn"
                        />
                        <Box display="flex" justifyContent="flex-end">
                          <Typography variant="caption">
                            {teacherData.characterName.length}/
                            {maxLengths.characterName}
                          </Typography>
                        </Box>

                        <Typography variant="formLabel">
                          Hälsningsfras*
                        </Typography>

                        <TextField
                          fullWidth
                          margin="normal"
                          name="greeting"
                          multiline
                          rows={1}
                          value={teacherData.greeting}
                          onChange={handleChange}
                          inputProps={{ maxLength: maxLengths.greeting }}
                          sx={{ ...styles.whiteBg, mt: 1 }}
                          placeholder="Hej, vad kan jag hjälpa dig med idag?"
                        />
                        <Box display="flex" justifyContent="flex-end">
                          <Typography variant="caption">
                            {teacherData.greeting.length}/{maxLengths.greeting}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4} sx={{ textAlign: "right" }}>
                        <input
                          accept="image/*"
                          type="file"
                          onChange={handleImageChange}
                          style={{ display: "none" }}
                          id="image-upload"
                        />
                        <label htmlFor="image-upload">
                          <IconButton
                            component="span"
                            sx={{
                              "&:hover": {
                                background: "none",
                                filter: "brightness(0.7)",
                              },
                            }}
                          >
                            <AddCircleIcon
                              sx={{
                                position: "absolute",
                                zIndex: 9999,
                                bottom: 6,
                                right: 6,
                                backgroundColor: "white",
                                borderRadius: "50px",
                                fontSize: "1.8rem",
                              }}
                            />
                            <Avatar
                              sx={{
                                width: 173,
                                height: 173,
                                mt: 3,
                                bgcolor: "#8b5cf6",
                                color: "white",
                                cursor: "pointer",
                                borderRadius: 4,
                              }}
                            >
                              {previewUrl ? (
                                <img
                                  src={previewUrl}
                                  alt="Preview"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <AddAPhotoIcon sx={{ fontSize: 40 }} />
                              )}
                            </Avatar>
                          </IconButton>
                        </label>
                      </Grid>
                    </Grid>
                    {currentStep !== 3 && (
                      <>
                        <Typography variant="formLabel" sx={{ mt: 1 }}>
                          Beskrivning av hur assistenten ska bete sig
                        </Typography>

                        <TextField
                          fullWidth
                          margin="normal"
                          name="background"
                          multiline
                          rows={9}
                          value={teacherData.background}
                          onChange={handleChange}
                          inputProps={{ maxLength: maxLengths.background }}
                          sx={{ ...styles.whiteBg, mt: 1 }}
                          placeholder="Hur vill du att karaktären ska tala och agera, hur bör den strukturera sina svar, och vilket temperament och ton ska den ha?"
                          // InputProps={{
                          //   endAdornment: (
                          //     <InputAdornment position='end'>
                          //       <IdeaGenerator
                          //         user={user}
                          //         onIdeaGenerated={handleIdeaGenerated}
                          //         systemMessage="Ge mig en karaktärsdefinition på 3-4 meningar på en chatbot, t.ex. 'Du är en spansklärare som är uppmuntrande, pedagogisk och förstående. Driv samtalet framåt och skapa en personlig relation med eleven med ett vänskapligt språkbruk och samtalston.'  Följ denna struktur varje gång: [{ 'idea': 'the  idea'}]"
                          //         position='top'
                          //         size='small'
                          //       />
                          //     </InputAdornment>
                          //   ),
                          // }}
                        />
                        <Box display="flex" justifyContent="flex-end">
                          <Typography variant="caption">
                            {teacherData.background.length}/
                            {maxLengths.background}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            mb: 5,
                            display: "flex",
                            justifyContent: "flex-start",
                          }}
                        >
                          <Button
                            variant="print"
                            onClick={() => setIsExampleDialogOpen(true)}
                            sx={{
                              ...styles.tightButton,
                              textTransform: "none",
                            }}
                            startIcon={<OpenInNewIcon />}
                          >
                            Visa exempelbeskrivningar
                          </Button>
                        </Box>
                      </>
                    )}
                    <ExampleDescriptionsDialog
                      open={isExampleDialogOpen}
                      onClose={() => setIsExampleDialogOpen(false)}
                      onSelectDescription={handleExampleSelect}
                      styles={styles}
                      theme={theme}
                    />
                    {/*
              <Tooltip title='Ju bättre beskrivning desto fler användare som kommer använda den'>
                <Typography variant='formLabel'>Beskrivning</Typography>
              </Tooltip>
              <TextField
                fullWidth
                margin='normal'
                name='description'
                multiline
                rows={2}
                value={teacherData.description}
                onChange={handleChange}
                inputProps={{ maxLength: maxLengths.description }}
                sx={{ ...styles.whiteBg, mt: 1 }}
                placeholder='Beskriv karaktären för andra användare'
              />
              <Box display='flex' justifyContent='flex-end'>
                <Typography variant='caption'>
                  {teacherData.description.length}/{maxLengths.description}
                </Typography>
              </Box>

              <Tooltip title='Kort och kärnfull beskrivning av karaktären'>
                <Typography variant='formLabel'>Tagline</Typography>
              </Tooltip>
              <TextField
                fullWidth
                margin='normal'
                name='tagline'
                value={teacherData.tagline}
                onChange={handleChange}
                inputProps={{ maxLength: maxLengths.tagline }}
                sx={{ ...styles.whiteBg, mt: 1 }}
                placeholder='Lägg till en kort tagline'
              />
              <Box display='flex' justifyContent='flex-end'>
                <Typography variant='caption'>
                  {teacherData.tagline.length}/{maxLengths.tagline}
                </Typography>
              </Box>
*/}

                    <FormControl fullWidth margin="normal" sx={{ mt: 0 }}>
                      <Typography variant="formLabelSinUnderline">
                        Röst
                      </Typography>

                      <Select
                        id="voice-select"
                        name="voice"
                        value={teacherData.voice || ""}
                        onChange={handleChange}
                        displayEmpty
                        sx={{ ...styles.whiteBg, mt: 1 }}
                      >
                        <MenuItem value="" disabled>
                          Välj röst
                        </MenuItem>

                        <ListSubheader
                          sx={{
                            color: "text.secondary",
                            bgcolor: (theme) =>
                              theme.palette.background.default + "90",
                          }}
                        >
                          Kvinnliga röster
                        </ListSubheader>

                        <MenuItem value="nova">Nova</MenuItem>
                        <MenuItem value="coral">Coral</MenuItem>
                        <MenuItem value="sage">Sage</MenuItem>
                        <MenuItem value="shimmer">Shimmer</MenuItem>
                        <ListSubheader
                          sx={{
                            color: "text.secondary",
                            bgcolor: (theme) =>
                              theme.palette.background.default + "90",
                          }}
                        >
                          Manliga röster
                        </ListSubheader>
                        <MenuItem value="alloy">Alloy</MenuItem>
                        <MenuItem value="ash">Ash</MenuItem>
                        <MenuItem value="echo">Echo</MenuItem>
                        <MenuItem value="verse">Verse</MenuItem>
                        <MenuItem value="ballad">Ballad</MenuItem>
                        <MenuItem value="onyx">Onyx</MenuItem>
                      </Select>
                    </FormControl>

                    {user.admin && (
                      <FormControl fullWidth margin="normal" sx={{ mt: 2 }}>
                        <Typography variant="formLabelSinUnderline">
                          Kategori
                        </Typography>

                        <Select
                          id="category"
                          name="category"
                          value={teacherData.category}
                          onChange={handleChange}
                          displayEmpty
                          sx={{ ...styles.whiteBg, mt: 1 }}
                        >
                          <MenuItem value="" disabled>
                            Välj kategori
                          </MenuItem>
                          {categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    <Button
                      onClick={() =>
                        setShowAdditionalFields(!showAdditionalFields)
                      }
                      variant="outlined"
                      endIcon={
                        showAdditionalFields ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )
                      }
                      sx={{
                        ...styles.beautyBtnTres,
                        mt: 4,
                        mb: 4,
                        display: "none",
                      }}
                      aria-expanded={showAdditionalFields}
                      aria-controls="additional-fields"
                    >
                      {showAdditionalFields
                        ? "Dölj fler alternativ"
                        : "Visa fler alternativ"}
                    </Button>

                    {showAdditionalFields && (
                      <>
                        <Box>
                          <Tooltip title="Beskriv lärarens pedagogiska metoder">
                            <Typography variant="formLabel">
                              Undervisningsstil
                            </Typography>
                          </Tooltip>
                          <TextField
                            fullWidth
                            margin="normal"
                            name="teachingStyle"
                            value={teacherData.teachingStyle}
                            onChange={handleChange}
                            inputProps={{
                              maxLength: maxLengths.teachingStyle,
                            }}
                            sx={{ ...styles.whiteBg, mt: 1 }}
                            placeholder="Tuff men rättvis"
                          />
                          <Box display="flex" justifyContent="flex-end">
                            <Typography variant="caption">
                              {teacherData.teachingStyle.length}/
                              {maxLengths.teachingStyle}
                            </Typography>
                          </Box>

                          <Tooltip title="Beskriv lärarens personlighetsdrag">
                            <Typography variant="formLabel">
                              Personlighet
                            </Typography>
                          </Tooltip>
                          <TextField
                            fullWidth
                            margin="normal"
                            name="personality"
                            value={teacherData.personality}
                            onChange={handleChange}
                            inputProps={{ maxLength: maxLengths.personality }}
                            sx={{ ...styles.whiteBg, mt: 1 }}
                            placeholder="Känslomässigt instabil men med ett gott hjärta"
                          />
                          <Box display="flex" justifyContent="flex-end">
                            <Typography variant="caption">
                              {teacherData.personality.length}/
                              {maxLengths.personality}
                            </Typography>
                          </Box>

                          <Tooltip title="Beskriv lärarens grundläggande pedagogiska övertygelser">
                            <Typography variant="formLabel">
                              Pedagogisk filosofi
                            </Typography>
                          </Tooltip>
                          <TextField
                            fullWidth
                            margin="normal"
                            name="pedagogicalPhilosophy"
                            multiline
                            rows={2}
                            value={teacherData.pedagogicalPhilosophy}
                            onChange={handleChange}
                            inputProps={{
                              maxLength: maxLengths.pedagogicalPhilosophy,
                            }}
                            sx={{ ...styles.whiteBg, mt: 1 }}
                            placeholder="Montessori-, waldorf- eller freinetpedagogik"
                          />
                          <Box display="flex" justifyContent="flex-end">
                            <Typography variant="caption">
                              {teacherData.pedagogicalPhilosophy.length}/
                              {maxLengths.pedagogicalPhilosophy}
                            </Typography>
                          </Box>

                          <Tooltip title="Beskriv hur läraren kommunicerar med studenter">
                            <Typography variant="formLabel">
                              Kommunikationsstil
                            </Typography>
                          </Tooltip>
                          <TextField
                            fullWidth
                            margin="normal"
                            name="communicationStyle"
                            value={teacherData.communicationStyle}
                            onChange={handleChange}
                            inputProps={{
                              maxLength: maxLengths.communicationStyle,
                            }}
                            sx={{ ...styles.whiteBg, mt: 1 }}
                            placeholder="T.ex. empatisk, lyhörd och inspirerande"
                          />
                          <Box display="flex" justifyContent="flex-end">
                            <Typography variant="caption">
                              {teacherData.communicationStyle.length}/
                              {maxLengths.communicationStyle}
                            </Typography>
                          </Box>

                          <Tooltip title="Beskriv hur läraren hanterar misstag och frågor från studenter">
                            <Typography variant="formLabel">
                              Förhållningssätt till misstag och frågor
                            </Typography>
                          </Tooltip>
                          <TextField
                            fullWidth
                            margin="normal"
                            name="approachToMistakes"
                            multiline
                            rows={2}
                            value={teacherData.approachToMistakes}
                            onChange={handleChange}
                            inputProps={{
                              maxLength: maxLengths.approachToMistakes,
                            }}
                            placeholder="T.ex. lösningsfokuserad, stöttande och positiv"
                            sx={{ ...styles.whiteBg, mt: 1 }}
                          />
                          <Box display="flex" justifyContent="flex-end">
                            <Typography variant="caption">
                              {teacherData.approachToMistakes.length}/
                              {maxLengths.approachToMistakes}
                            </Typography>
                          </Box>

                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                              background:
                                theme.palette.mode === "dark"
                                  ? "#2a3742"
                                  : "#fff",
                              marginBottom: "5px",
                              borderRadius: "10px",
                              padding: "12px",
                              paddingTop: "5px",
                              paddingBottom: "5px",
                              mt: 2,
                            }}
                          >
                            <Tooltip title="Extra tydlig, långsamt samtalstempo, bekräftar förståelse, upprepar information och ger god tid för reflektion.">
                              <Typography variant="formLabel">
                                Dyslexi-anpassad
                              </Typography>
                            </Tooltip>

                            <Switch
                              checked={teacherData.dyslexiaAdapted}
                              onChange={handleChange}
                              name="dyslexiaAdapted"
                            />
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                              background:
                                theme.palette.mode === "dark"
                                  ? "#2a3742"
                                  : "#fff",
                              marginBottom: "5px",
                              borderRadius: "10px",
                              padding: "12px",
                              paddingTop: "5px",
                              paddingBottom: "5px",
                              mt: 2,
                            }}
                          >
                            <Tooltip title="Korta instruktioner, bryter ner uppgifter i mindre delar, förutsägbar miljö, positiv feedback & förståelse.">
                              <Typography variant="formLabel">
                                ADHD-anpassad
                              </Typography>
                            </Tooltip>
                            <Switch
                              checked={teacherData.adhdAdapted}
                              onChange={handleChange}
                              name="adhdAdapted"
                            />
                          </Box>
                        </Box>
                      </>
                    )}

                    {user.admin && (
                      <>
                        <Typography
                          variant="formLabelSinUnderline"
                          sx={{ display: "block", mt: 2 }}
                        >
                          Synlighet
                        </Typography>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{
                            background:
                              theme.palette.mode === "dark"
                                ? "#2a3742"
                                : "#fff",
                            marginBottom: "5px",
                            borderRadius: "10px",
                            padding: "12px",
                            paddingTop: "5px",
                            paddingBottom: "5px",
                            mt: 1,
                          }}
                        >
                          <Box display="flex" alignItems="center">
                            <Tooltip
                              title="Ska karaktären vara tillgänglig för andra att använda?"
                              placement="top"
                            >
                              <Typography variant="formLabel">
                                Synlighet
                              </Typography>
                            </Tooltip>
                            {visibility === "public" ? (
                              <PublicIcon
                                sx={{ marginLeft: 1, fontSize: "20px" }}
                              />
                            ) : (
                              <LockIcon
                                sx={{ marginLeft: 1, fontSize: "20px" }}
                              />
                            )}
                          </Box>
                          <Box display="flex" alignItems="center">
                            <Typography variant="caption" sx={{ mr: 1 }}>
                              {visibility === "public" ? "Publik" : "Privat"}
                            </Typography>
                            <Switch
                              checked={visibility === "public"}
                              onChange={(e) =>
                                setVisibility(
                                  e.target.checked ? "public" : "private",
                                )
                              }
                              name="visibility"
                            />
                          </Box>
                        </Box>

                        <FormControl fullWidth margin="normal" sx={{ mt: 2 }}>
                          <Typography variant="formLabelSinUnderline">
                            Typ
                          </Typography>
                          <Select
                            id="type-select"
                            name="type"
                            value={teacherData.type || ""}
                            onChange={handleChange}
                            displayEmpty
                            sx={{ ...styles.whiteBg, mt: 1, mb: 2 }}
                          >
                            <MenuItem value="" disabled>
                              Välj typ
                            </MenuItem>
                            <MenuItem value="Debatt">Debatt</MenuItem>
                            <MenuItem value="Dialog">Dialog</MenuItem>
                            <MenuItem value="Rollspel">Rollspel</MenuItem>
                            <MenuItem value="Karaktär">Karaktär</MenuItem>
                            <MenuItem value="Bok">Bok</MenuItem>
                          </Select>
                        </FormControl>

                        <Typography variant="formLabel">
                          Språk (för språk-assistenter):
                        </Typography>
                        <TextField
                          fullWidth
                          margin="normal"
                          name="languageToLearn"
                          value={teacherData.languageToLearn || ""}
                          onChange={handleChange}
                          sx={{ ...styles.whiteBg, mt: 1, mb: 2 }}
                        />

                        <Typography variant="formLabel">
                          Ämne (för traditionella lärar-assistenter):
                        </Typography>
                        <TextField
                          fullWidth
                          margin="normal"
                          name="traditionalTeacherSubject"
                          value={teacherData.traditionalTeacherSubject || ""}
                          onChange={handleChange}
                          sx={{ ...styles.whiteBg, mt: 1, mb: 2 }}
                        />

                        <Typography variant="formLabel">
                          Kort beskrivning (visas på kortet)
                        </Typography>
                        <TextField
                          fullWidth
                          margin="normal"
                          name="cardDescription"
                          multiline
                          rows={2}
                          value={teacherData.cardDescription || ""}
                          onChange={handleChange}
                          inputProps={{
                            maxLength: maxLengths.cardDescription,
                          }}
                          sx={{ ...styles.whiteBg, mt: 1 }}
                          placeholder="En kort beskrivning som visas på assistentens kort"
                        />
                        <Box display="flex" justifyContent="flex-end">
                          <Typography variant="caption">
                            {(teacherData.cardDescription || "").length}/
                            {maxLengths.cardDescription}
                          </Typography>
                        </Box>

                        <Box display="flex" justifyContent="flex-end">
                          <Typography variant="caption">
                            {teacherData.cardDescription.length}/
                            {maxLengths.cardDescription}
                          </Typography>
                        </Box>

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{
                            background:
                              theme.palette.mode === "dark"
                                ? "#2a3742"
                                : "#fff",
                            marginBottom: "5px",
                            borderRadius: "10px",
                            padding: "12px",
                            paddingTop: "5px",
                            paddingBottom: "5px",
                            mt: 2,
                          }}
                        >
                          <Tooltip title="Tillåter assistenten att ha tillgång till outside sources">
                            <Typography variant="formLabel">
                              RAG-aktiverad
                            </Typography>
                          </Tooltip>
                          <Switch
                            checked={teacherData.rag}
                            onChange={handleChange}
                            name="rag"
                          />
                        </Box>

                        <Box sx={{ mt: 3 }}>
                          <Typography variant="formLabelSinUnderline">
                            Bakgrundsbild
                          </Typography>
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <input
                              accept="image/*"
                              type="file"
                              onChange={handleBackgroundImageChange}
                              style={{ display: "none" }}
                              id="background-image-upload"
                            />
                            <label htmlFor="background-image-upload">
                              <Button
                                variant="outlined"
                                component="span"
                                startIcon={<AddAPhotoIcon />}
                                sx={{ mb: 2 }}
                              >
                                Ladda upp bakgrundsbild
                              </Button>
                            </label>
                            {backgroundPreviewUrl && (
                              <Box sx={{ mt: 1, width: "100%", maxWidth: 500 }}>
                                <img
                                  src={backgroundPreviewUrl}
                                  alt="Background Preview"
                                  style={{
                                    width: "100%",
                                    maxHeight: "200px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                  }}
                                />
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </>
                    )}
                    <Button
                      onClick={handleSubmit}
                      variant={
                        theme.palette.mode === "light" ? "purp" : "contained"
                      }
                      color="primary"
                      style={{ marginTop: "23px" }}
                      disabled={isLoading}
                      startIcon={
                        isLoading ? (
                          <CircularProgress sx={{ color: "white" }} size={16} />
                        ) : (
                          <AddCircleOutlineIcon />
                        )
                      }
                    >
                      {teacherId ? "Uppdatera assistent" : "Skapa assistent"}
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: "none" }}>
                  {/* Guiden-sektionen */}
                  <Box sx={{ padding: { xs: "0px", md: "20px" } }}>
                    <GuideSection theme={theme} styles={styles} />
                  </Box>
                </Grid>
              </Grid>
            </AnimatedStep>
          </>
        )}
      </Box>
      <LoadingDialog
        open={isLoading}
        onClose={() => {}} // You can add a function here if needed
        title={
          currentStep === 3
            ? "Tränar assistenten på ditt innehåll..."
            : "Skapar assisteten..."
        }
        progressLabel={
          currentStep === 3
            ? "Tränar assistenten på ditt innehåll..."
            : "Skapar assisteten..."
        }
        snackbarMessage="Genereringen är snart klar. Vänligen vänta."
        snackbarSeverity="info"
        progressBarHeight="15px"
        progressBarFontSize="0.9rem"
        coffee={true}
      />
    </Box>
  );
};

export default TeacherHandler;
